import {
    InsertDriveFile as FileIcon,
    Description as DocIcon,
    TableChart as XlsIcon,
    PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { LinearProgress, Typography } from '@mui/material';
import { Box } from '@mui/system';
import styled from 'styled-components';

import { type ExtendedFile } from './FileDragDrop';

const FilePreview = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 200px;
    text-align: center;

    &.uploading .fileIcon:not(.failed) {
        animation: pulsate 1.5s infinite ease-in-out;
        opacity: 0.5;
    }

    .fileIcon {
        position: relative;

        &.failed {
            &:before {
                content: '!';
                color: red;
                display: flex;
                width: 100%;
                height: 100%;
                position: absolute;
                align-items: center;
                justify-content: center;
                font-size: 32px;
            }
        }
    }

    @keyframes pulsate {
        0% {
            opacity: 0.5;
        }
        50% {
            opacity: 1;
        }
        100% {
            opacity: 0.5;
        }
    }
`;

const FileGrid = styled(Box)`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-top: 16px;
`;

const WrappedTypography = styled(Typography)`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-wrap: break-word;
    max-width: 100%;
`;

const ImgThumb = styled('img')`
    width: 160px;
    height: auto;
    max-height: 120px;
    border-radius: 4px;
`;

type SelectedFilesProps = {
    files: ExtendedFile[];
};
const getFileIcon = (extendedFile: ExtendedFile) => {
    const { file } = extendedFile;
    const fileType = file.type;
    if (!fileType) {
        console.error('Missing file type', file);
        return null;
    }
    if (fileType.startsWith('image/')) {
        return <ImgThumb src={URL.createObjectURL(file)} alt={file.name} />;
    } else if (fileType === 'application/pdf') {
        return <PdfIcon fontSize="large" color="error" />;
    } else if (fileType.includes('word')) {
        return <DocIcon fontSize="large" color="primary" />;
    } else if (fileType.includes('excel') || fileType.includes('spreadsheet')) {
        return <XlsIcon fontSize="large" color="success" />;
    }
    return <FileIcon fontSize="large" color="disabled" />;
};

export const SelectedFiles = (props: SelectedFilesProps) => {
    const { files } = props;
    return (
        <FileGrid>
            {files.map((file, index) => (
                <FilePreview key={index} className={file?.uploading ? 'uploading' : ''}>
                    <Box className={`fileIcon ${file?.failed ? 'failed' : ''}`}>{getFileIcon(file)}</Box>
                    <Box sx={{ width: '160px' }}>
                        <LinearProgress variant="determinate" value={file?.progress ?? 0} color="primary" />
                    </Box>
                    <WrappedTypography variant="caption">{file.file.name}</WrappedTypography>
                </FilePreview>
            ))}
        </FileGrid>
    );
};
